/**
 * One attempt at one table (UI-002) — the state machine the whole app is driven by.
 *
 *   aiming → simulating → animating → aiming (or complete)
 *
 * The important thing this type does is HOLD THE ANSWER BACK. `simulateShot` returns
 * the entire shot the instant it is called, and `resolveShot` can score it just as
 * fast — so by the time the first frame is drawn, the balls-remaining count and the
 * win screen are already decided. Showing either of them then would tell the player
 * what happened before they watched it happen (ARCHITECTURE.md §5.1).
 *
 * So `take()` simulates and stashes the resolution, and `settle()` — called when the
 * animation clock reaches the end, and only then — applies it and says what happened.
 * Nothing else may read the pending resolution, and there is no accessor for it.
 *
 * The rules themselves are in `@potted/sim` (rules.ts), not here, because the Phase 4
 * Worker has to reach the same verdict from the same shots.
 */
import {
  type BallId,
  resolveShot,
  simulateShot,
  startRun,
  stateFromSnapshots,
  type BallSnapshot,
  type Layout,
  type PhysicsConfig,
  type RunState,
  type ShotInput,
  type ShotOutcome,
  type ShotResolution,
  type SimResult,
  type TableGeometry,
} from '@potted/sim';

export type Phase =
  /** Waiting for the player. The only phase that accepts input. */
  | 'aiming'
  /** `simulateShot` is running. Brief, but a real state: it is where a stall shows. */
  | 'simulating'
  /** The shot is playing out on screen. The outcome exists but is not readable. */
  | 'animating'
  /** Every object ball is down. */
  | 'complete';

export interface Session {
  phase(): Phase;
  run(): RunState;
  balls(): readonly BallSnapshot[];
  /**
   * Simulate a shot and return it to be animated. The run is NOT advanced: the score,
   * the count and the win screen stay as they were until `settle()`.
   */
  take(input: ShotInput): SimResult;
  /** Apply the shot that has just finished animating. Throws if none is pending. */
  settle(): ShotOutcome;
  /** Swap in the physics config, for the debug panel's sliders. */
  usePhysics(config: PhysicsConfig): void;
  /**
   * Debug ball in hand (DBG-002, any ball since DBG-005): move ball `id`, between shots
   * only. The caller checks the spot is legal (`ball-in-hand.ts`). Not a rule of the
   * game: a run that uses it will not replay on the server, which is correct.
   */
  placeBall(id: BallId, xM: number, yM: number): void;
}

export interface SessionOptions {
  /**
   * Free play (UI-012): the table never "completes", so a cleared table with only the
   * cue ball on it keeps taking shots. Nothing about such a session is a run that
   * counts; the caller never saves or sends it.
   */
  readonly endless?: boolean;
}

export function createSession(
  layout: Layout,
  geometry: TableGeometry,
  physics: PhysicsConfig,
  options: SessionOptions = {},
): Session {
  let run = startRun(layout);
  let phase: Phase = 'aiming';
  let pending: ShotResolution | undefined;
  let config = physics;

  return {
    phase: () => phase,
    run: () => run,
    balls: () => run.balls,

    take(input) {
      if (phase !== 'aiming') {
        throw new Error(`session: a shot was taken while ${phase}`);
      }
      phase = 'simulating';
      const result = simulateShot(stateFromSnapshots(run.balls), input, config, geometry);
      pending = resolveShot(run, result, geometry, config);
      phase = 'animating';
      return result;
    },

    settle() {
      if (pending === undefined) {
        throw new Error('session: settle() with no shot in flight');
      }
      const { run: next, outcome } = pending;
      pending = undefined;
      run = next;
      phase = next.complete && options.endless !== true ? 'complete' : 'aiming';
      return outcome;
    },

    usePhysics(next) {
      config = next;
    },

    placeBall(id, xM, yM) {
      if (phase !== 'aiming') throw new Error(`session: a ball was moved while ${phase}`);
      run = {
        ...run,
        balls: run.balls.map((ball) =>
          ball.id === id && !ball.potted ? { ...ball, xM, yM } : ball,
        ),
      };
    },
  };
}
